<template>
  <form @submit.prevent="next">
    <h1 class="text-primary">{{ $t("global.checkout-billing") }}</h1>
    <Alert variant="danger" v-if="errors" @close="errors = null">
      {{ errors }}
    </Alert>
    <div class="my-4">
      <h3 class="text-primary">{{ $t("global.billing-contact") }}</h3>
      <div class="text-secondary mb-3">{{ $t("global.enter-billing-contact") }}</div>
    </div>
    <div class="form-row">
      <FormInput
        class="col-md-6"
        name="first-name"
        v-model="billingFields.firstName"
        test-id="first-name-input"
        :label="`${$t('global.first-name')} *`"
        :errors="validation.fields.firstName.errors"
        :dirty="validation.fields.firstName.dirty"
      />
      <FormInput
        class="col-md-6"
        name="last-name"
        v-model="billingFields.lastName"
        test-id="last-name-input"
        :label="`${$t('global.last-name')} *`"
        :errors="validation.fields.lastName.errors"
        :dirty="validation.fields.lastName.dirty"
      />
    </div>

    <div class="form-row">
      <FormInput
        class="col-md-6"
        name="phone"
        v-model="billingFields.phone"
        test-id="phone-input"
        :label="`${$t('global.phone')} *`"
        :errors="validation.fields.phone.errors"
        :dirty="validation.fields.phone.dirty"
      />
      <FormInput
        class="col-md-6"
        name="company"
        v-model="billingFields.company"
        test-id="company-input"
        :label="$t('global.company')"
        :errors="validation.fields.company.errors"
        :dirty="validation.fields.company.dirty"
      />
    </div>

    <div class="form-row">
      <FormInput
        class="col-md-6"
        name="address1"
        v-model="billingFields.address1"
        test-id="address1-input"
        :label="`${$t('global.address')} *`"
        :errors="validation.fields.address1.errors"
        :dirty="validation.fields.address1.dirty"
      />
      <FormInput
        class="col-md-6"
        name="address2"
        v-model="billingFields.address2"
        test-id="address2-input"
        :label="$t('global.address2')"
        :errors="validation.fields.address2.errors"
        :dirty="validation.fields.address2.dirty"
      />
    </div>

    <div class="form-row">
      <FormInput
        class="col-md-6"
        name="city"
        v-model="billingFields.city"
        test-id="city-input"
        :label="`${$t('global.city')} *`"
        :errors="validation.fields.city.errors"
        :dirty="validation.fields.city.dirty"
      />
      <FormSelect
        class="col-md-6"
        name="state"
        v-model="billingFields.state"
        test-id="state-input"
        :label="`${$t('global.state')} *`"
        :errors="validation.fields.state.errors"
        :dirty="validation.fields.state.dirty"
      >
        <option selected="true" value=""> - {{ $t("global.select-state") }} - </option>
        <option
          :value="state.code"
          v-for="state in states"
          :key="state.code"
          :selected="billingFields.state === state.code"
        >
          {{ state.name }}
        </option>
      </FormSelect>
    </div>

    <div class="form-row">
      <FormInput
        class="col-md-6"
        name="zip"
        v-model="billingFields.zip"
        test-id="zip-input"
        :label="`${$t('global.zip')} *`"
        :errors="validation.fields.zip.errors"
        :dirty="validation.fields.zip.dirty"
      />
      <FormSelect
        class="col-md-6"
        name="country"
        v-model="billingFields.country"
        test-id="country-input"
        :label="`${$t('global.country')} *`"
        :errors="validation.fields.country.errors"
        :dirty="validation.fields.country.dirty"
      >
        <option selected="true" value=""> - {{ $t("global.select-country") }} - </option>
        <option
          v-for="country in countries"
          :value="country.code"
          :key="country.code"
          :selected="billingFields.country === country.code"
        >
          {{ country.name }}
        </option>
      </FormSelect>
    </div>

    <div v-if="showTaxExemptionForm" class="form-row my-4 d-flex flex-column">
      <div class="d-flex">
        <h3 class="text-primary">{{ $t("global.tax-status") }}</h3>
        <Tooltip :content="$t('global.tax-status-info')">
          <Icon icon="info" color="gamma" class="ml-2" />
        </Tooltip>
      </div>
      <FormCheckbox
        v-model="billingFields.isTaxExempt"
        :label="$t('global.tax-exempt')"
        @input="resetTaxExemptForm"
      />
      <template v-if="billingFields.isTaxExempt">
        <FormInput
          class="col-md-6"
          name="tax-exemption-id"
          v-model="billingFields.taxId"
          test-id="tax-exemption-id-input"
          :label="`${$t('global.tax-exemption-id')} *`"
          :errors="validation.fields.taxId.errors"
          :dirty="validation.fields.taxId.dirty"
        />
        <Dropzone
          v-model="taxFileIds"
          @sending="uploadingTaxFiles"
          @complete="completedTaxFileUpload"
        >
          <div class="my-4 p-4 tax-documents">
            <span class="mr-2 my-2 text-secondary">{{ $t("global.attach-tax-documents") }}:</span>
            <Btn id="dropzone-upload-file" class="mr-2 my-2" :loading="taxFilesLoading">
              {{ $t("global.browse-files") }}
            </Btn>
            <div class="dropzone-previews"></div>
          </div>
        </Dropzone>
      </template>
    </div>

    <div class="text-right my-4">{{ `* ${$t("global.required")}` }}</div>
    <div class="mt-4 text-right">
      <Btn class="mr-2" variant="light" :disabled="loading" @click="back">
        {{ $t("global.back") }}
      </Btn>
      <Btn variant="primary" type="submit" :loading="loading" @click="next">
        {{ $t("global.next") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import { checkoutBilling as billingValidationRules } from "leatherman-js/utils/validations/forms";
import Btn from "@/components/display/Btn";
import constants from "@/utils/constants";
import Dropzone from "@/components/display/Dropzone";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormSelect from "@/components/display/form/FormSelect";
import Icon from "@/components/display/icon/Icon";
import { isTaxExemptCountry } from "leatherman-js/utils/validations/rules/taxExempt";
import { parseNetworkError } from "@/utils/errorParse";
import store from "@/store";
import Tooltip from "@/components/display/Tooltip";
import useAnalytics from "@/utils/useAnalytics";
import useValidator from "@/utils/useValidator";

export default {
  name: "CheckoutBillingPage",
  components: {
    Alert,
    Btn,
    Icon,
    Dropzone,
    FormCheckbox,
    FormInput,
    FormSelect,
    Tooltip,
  },
  setup(props, context) {
    const loading = ref(false);
    const errors = ref(null);
    const user = computed(() => store.state.auth.user);
    const isTaxExempt = computed(() => store.getters["tax/getIsTaxExempt"]);
    const taxId = computed(() => store.getters["tax/getTaxId"]);
    const billingAddress = computed(() => store.getters["addresses/getBillingAddress"]);
    const requireStoreBuilder = computed(() => store.getters["checkout/requireStoreBuilder"]);

    // login variables and validation
    const billingFields = reactive({
      type: "billing",
      email: user.value?.email,
      firstName: null,
      lastName: null,
      phone: null,
      company: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      zip: null,
      country: null,
      isTaxExempt: false,
      taxId: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      billingFields,
      billingValidationRules,
      {},
      {
        firstName: {
          required: context.root.$t("validation.first-name-required"),
        },
        lastName: {
          required: context.root.$t("validation.last-name-required"),
        },
        phone: {
          required: context.root.$t("validation.phone-required"),
          integer: context.root.$t("validation.invalid-phone"),
        },
        address1: {
          required: context.root.$t("validation.address-required"),
        },
        city: {
          required: context.root.$t("validation.city-required"),
        },
        state: {
          required: context.root.$t("validation.state-required"),
          regex: context.root.$t("validation.invalid-state"),
        },
        zip: {
          required: context.root.$t("validation.zip-required"),
          integer: context.root.$t("validation.invalid-zip"),
        },
        country: {
          required: context.root.$t("validation.country-required"),
          regex: context.root.$t("validation.invalid-country"),
        },
        taxId: {
          requiredIf: context.root.$t("validation.tax-id-required"),
          taxExempt: context.root.$t("validation.invalid-tax-id"),
        },
      }
    );

    const countries = Object.keys(constants.COUNTRIES).map((code) => {
      return { code, name: constants.COUNTRIES[code] };
    });

    const states = Object.keys(constants.STATES).map((code) => {
      return { code, name: constants.STATES[code] };
    });

    // tax form stuff
    const taxFileIds = ref([]);
    const showTaxExemptionForm = computed(() => isTaxExemptCountry(billingFields.country));
    const resetTaxExemptForm = () => validation.reset();
    const taxFilesLoading = ref(false);
    const uploadingTaxFiles = () => (taxFilesLoading.value = true);
    const completedTaxFileUpload = () => (taxFilesLoading.value = false);

    // go to previous checkout step
    const back = () => {
      if (requireStoreBuilder.value) {
        context.root.$router.push({
          name: "checkout-storebuilder",
          query: store.getters["checkout/getQueryParams"],
        });
      } else {
        context.root.$router.push({
          name: "checkout-details",
          query: store.getters["checkout/getQueryParams"],
        });
      }
    };

    const { cartData } = useAnalytics();

    // add billing info (address) and go to next checkout step
    const next = () => {
      loading.value = true;
      errors.value = false;
      validation.reset();
      validation
        .validate()
        .then(() => {
          // if billing address already exists, update it instead of creating a new one
          if (billingAddress.value) {
            // include the existing address' id in the request
            return store.dispatch("addresses/update", {
              id: billingAddress.value.id,
              data: billingFields,
            });
          } else {
            // hardcode the address type to "billing"
            return store.dispatch("addresses/create", billingFields);
          }
        })
        .then(() => {
          // check if the tax exemption checkbox is selected
          // if it is, validate the tax id provided
          if (billingFields.isTaxExempt) {
            return store
              .dispatch("tax/validateTaxId", {
                taxId: billingFields.taxId,
                countryCode: billingFields.country,
              })
              .then((resp) => {
                // if the tax is valid for the country provided
                if (resp.verified) {
                  return Promise.resolve();
                }
                validation.fields.taxId.dirty = true;
                validation.fields.taxId.errors = [context.root.$t("global.invalid-tax-id")];
                return Promise.reject();
              })
              .then(() =>
                store
                  .dispatch("tax/setTaxId", {
                    taxFileIds: taxFileIds.value,
                    taxId: billingFields.taxId,
                  })
                  .then(() => store.dispatch("auth/clientInfo"))
              );
          }
          return Promise.resolve();
        })
        .then(() => {
          store.dispatch("analytics/send", { name: "checkoutBillingInfo", data: cartData.value });

          // after address and tax info have been submitted, go to the payment step
          context.root.$router.push({
            name: "checkout-payment",
            query: store.getters["checkout/getQueryParams"],
          });
        })
        .catch((e) => {
          errors.value = parseNetworkError(e);
        })
        .finally(() => (loading.value = false));
    };

    onMounted(() => {
      if (
        !store.state.checkout.appId ||
        !store.state.checkout.packageId ||
        !store.state.checkout.locationId ||
        (!requireStoreBuilder.value && !store.state.checkout.nickname) ||
        (requireStoreBuilder.value && !store.state.checkout.storeBuilderId) ||
        !store.getters["auth/isLoggedIn"]
      ) {
        back();
      }

      store.dispatch("addresses/getList").then(() => {
        // if a billing address already exists
        if (billingAddress.value) {
          // pre-populate the form with the existing billing address
          // computed values aren't allowed as defaults, which is why these are being set down here after the fetch has completed
          Object.keys(billingFields).forEach((addressKey) => {
            billingFields[addressKey] = billingAddress.value[addressKey];
          });

          if (isTaxExempt.value) {
            billingFields.isTaxExempt = true;
            billingFields.taxId = taxId.value;
          }
        }
      });
    });

    return {
      loading,
      errors,
      billingFields,
      next,
      back,
      countries,
      states,
      validation,
      showTaxExemptionForm,
      resetTaxExemptForm,
      taxFilesLoading,
      uploadingTaxFiles,
      completedTaxFileUpload,
      taxFileIds,
    };
  },
};
</script>
<style lang="scss">
@import "~@/styles/variables";
</style>
